/* AUTO-SYNCED from PsyKit.io/lib/haptics/useGamepadHaptics.ts — DO NOT EDIT. Regenerate with `npm run sync`. */
"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  firstUsableGamepad,
  gamepadLabel,
  isGamepadApiSupported,
  type UsableGamepad,
} from "./gamepad-poll";

export type HapticsStatus = "unsupported" | "no-controller" | "ready";

export interface UseGamepadHapticsOptions {
  /** When false, the hook stops driving the actuator and releases it. */
  enabled: boolean;
  /** Pulse amplitude 0..1. Mapped to strongMagnitude (left pulses) or
   *  weakMagnitude (right pulses). 0 silences without disabling polling. */
  intensity: number;
  /** Pulse duration in ms. Default 120 — past the rumble motor's ~50-100ms
   *  startup ramp so each pulse hits steady-state, but short enough to
   *  leave a clear silent gap before the opposite-side pulse fires. At the
   *  default 1 Hz (500ms half-cycle), 120ms pulse → 380ms gap, which reads
   *  as crisp "left … right … left … right" rather than continuous rumble. */
  pulseMs?: number;
}

export interface UseGamepadHapticsResult {
  /** Imperative trigger called from BallCanvas/MirrorCanvas onPassComplete(side).
   *  Always safe to call — no-ops when disabled, unsupported, or no controller. */
  pulse: (side: "left" | "right") => void;
  /** UI badge state — drives the HapticsStatus component. */
  status: HapticsStatus;
  /** Cleaned controller id (e.g. "DualSense Wireless Controller"), or null
   *  when no controller is exposed. */
  controllerLabel: string | null;
}

/**
 * Drives a connected game controller's rumble motors as a tactile-pulser
 * layer for BLS sessions. The heavy (strongMagnitude) motor sits in the
 * LEFT grip and the light (weakMagnitude) motor sits in the RIGHT grip on
 * both DualSense and Xbox Wireless families, so a single mapping gives the
 * right hand for both.
 *
 * Browser caveat: a paired controller is invisible to navigator.getGamepads()
 * until the user PRESSES A BUTTON on it. Until then `status` reports
 * "no-controller" and the UI nudges the user to press a button.
 */
export function useGamepadHaptics({
  enabled,
  intensity,
  pulseMs = 120,
}: UseGamepadHapticsOptions): UseGamepadHapticsResult {
  const [status, setStatus] = useState<HapticsStatus>(() =>
    isGamepadApiSupported() ? "no-controller" : "unsupported"
  );
  const [controllerLabel, setControllerLabel] = useState<string | null>(null);

  // Hot-path state — kept in refs so pulse() never triggers a render and
  // so the imperative callback always reads the latest options without
  // remounting the listener effect.
  const padRef = useRef<UsableGamepad | null>(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const intensityRef = useRef(intensity);
  intensityRef.current = intensity;
  const pulseMsRef = useRef(pulseMs);
  pulseMsRef.current = pulseMs;

  // Refresh padRef + status from a fresh navigator.getGamepads() snapshot.
  const refreshController = useCallback(() => {
    if (!isGamepadApiSupported()) {
      padRef.current = null;
      setStatus("unsupported");
      setControllerLabel(null);
      return;
    }
    const next = firstUsableGamepad();
    padRef.current = next;
    if (next) {
      setControllerLabel(gamepadLabel(next.gamepad));
      setStatus("ready");
    } else {
      setControllerLabel(null);
      setStatus("no-controller");
    }
  }, []);

  // Listen for connection lifecycle. Listeners stay registered as long as
  // the consumer is mounted — cost is trivial and lets us catch a controller
  // that's plugged in mid-session.
  useEffect(() => {
    if (!isGamepadApiSupported()) {
      setStatus("unsupported");
      return;
    }
    refreshController();
    const onConnect = () => refreshController();
    const onDisconnect = () => refreshController();
    window.addEventListener("gamepadconnected", onConnect);
    window.addEventListener("gamepaddisconnected", onDisconnect);
    return () => {
      window.removeEventListener("gamepadconnected", onConnect);
      window.removeEventListener("gamepaddisconnected", onDisconnect);
    };
  }, [refreshController]);

  // When the toggle flips off, best-effort release of the actuator so any
  // in-flight pulse stops immediately rather than playing out its 120ms tail.
  useEffect(() => {
    if (enabled) return;
    const actuator = padRef.current?.actuator;
    if (actuator?.reset) {
      actuator.reset().catch(() => {});
    }
  }, [enabled]);

  const pulse = useCallback((side: "left" | "right") => {
    if (!enabledRef.current) return;
    const pad = padRef.current;
    if (!pad) return;
    const amp = Math.max(0, Math.min(1, intensityRef.current));
    if (amp === 0) return;
    // ── Single-motor directional pulse, with right-side compensation ──
    // Earlier attempt added a "secondary motor" fade to extract more total
    // energy at high slider values. Clinical field test reported it blurred
    // the L/R distinction — the asymmetric magnitudes still read as "both
    // sides at once" because the controller body transmits vibration between
    // grips. Reverted to pure single-motor for unmistakable directionality.
    //
    // strongMagnitude = heavy/low-freq motor → LEFT grip on DualSense + Xbox
    // weakMagnitude   = light/high-freq motor → RIGHT grip on both
    //
    // Perceived-intensity compensation works on two levers:
    //
    // 1) Magnitude boost — the right (weak) motor is physically smaller and
    //    resonates at a higher frequency, so the same input reads as softer.
    //    Scale right-side magnitude up. Clamped to 1.0 (playEffect rejects
    //    values >1), so this only helps below the slider ceiling.
    //
    // 2) Pulse-duration extension — at the magnitude ceiling, the only
    //    remaining way to deliver more felt energy is to hold the pulse
    //    longer. The voice-coil motors in DualSense scale perceived intensity
    //    with sustained vibration time, so a 1.5× longer right pulse closes
    //    the felt gap without touching the L/R routing. At 0.8 Hz (the
    //    suggested pacing on toggle-on, 625 ms per side) a 180 ms right
    //    pulse still leaves ~445 ms silent before the next left pulse — L/R
    //    distinction stays crisp. At the slider's faster end (≥1.7 Hz) the
    //    gap tightens; that's an acceptable trade since fast pacing already
    //    blurs L/R perception regardless.
    const RIGHT_BOOST = 1.6;
    const RIGHT_PULSE_MULTIPLIER = 1.5;
    const baseDur = pulseMsRef.current;
    const rightMag = Math.min(1, amp * RIGHT_BOOST);
    const rightDur = Math.round(baseDur * RIGHT_PULSE_MULTIPLIER);
    const params =
      side === "left"
        ? { duration: baseDur,  strongMagnitude: amp, weakMagnitude: 0 }
        : { duration: rightDur, strongMagnitude: 0,   weakMagnitude: rightMag };
    // playEffect returns a promise — we never await it (would block the
    // animation loop) and silently swallow errors (browser surfaces them
    // for things like "actuator busy" which are not actionable here).
    pad.actuator.playEffect("dual-rumble", params).catch(() => {});
  }, []);

  return { pulse, status, controllerLabel };
}
